import { AdminList, AdminDataGrid, GridColumn } from './shared';
/**
 * Bonus Usage List Component
 * Displays all users with active bonus usage allowances
 */

import {
  DateField,
  SearchInput,
  SelectInput,
  useRecordContext,
} from 'react-admin';
import { Chip, Box, Typography, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import { FEATURE_LABELS, USAGE_FEATURE_CHOICES } from '../constants';
import { ListActions, PlanTierField } from './shared';

/**
 * Custom field: bonus usage chips per feature
 */
function BonusChipsField({ label: _label }: { label?: string } = {}) {
  const record = useRecordContext();
  if (!record?.bonusUsage) return null;

  const bonusUsage = record.bonusUsage as Record<string, number>;
  const activeFeatures = Object.entries(bonusUsage).filter(([, val]) => val > 0);

  if (activeFeatures.length === 0) {
    return <Typography variant="body2" color="textSecondary">None</Typography>;
  }

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      {activeFeatures.map(([feature, amount]) => (
        <Tooltip key={feature} title={feature}>
          <Chip
            label={`${FEATURE_LABELS[feature] || feature}: +${amount}`}
            size="small"
            color="info"
            variant="filled"
          />
        </Tooltip>
      ))}
    </Box>
  );
}

/**
 * Custom field: clickable user email linking to usage detail screen
 */
function LinkedUserEmailField({ label: _label }: { label?: string } = {}) {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Link
      component={RouterLink}
      to={`/users/${record.userId}/usage`}
      underline="hover"
    >
      {record.email}
    </Link>
  );
}

/**
 * Custom field: clickable user name linking to user detail
 */
function UserNameField({ label: _label }: { label?: string } = {}) {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Link
      component={RouterLink}
      to={`/users/${record.userId}/show`}
      underline="hover"
      color="textSecondary"
    >
      {record.name || '-'}
    </Link>
  );
}

/**
 * Filters for bonus usage list
 */
const bonusUsageFilters = [
  <SearchInput key="search" source="q" alwaysOn placeholder="Search by email or name..." />,
  <SelectInput
    key="feature"
    source="feature"
    label="Feature"
    choices={USAGE_FEATURE_CHOICES}
  />,
];

export function BonusUsageList() {
  return (
    <AdminList
      resource="bonus-usage"
      filters={bonusUsageFilters}
      actions={<ListActions />}
      sort={{ field: 'updatedAt', order: 'DESC' }}
      perPage={25}
      title="Bonus Usage"
    >
      <AdminDataGrid bulkActionButtons={false}>
        <GridColumn label="Email" source="email"><LinkedUserEmailField label="Email" /></GridColumn>
        <GridColumn label="Name" source="name"><UserNameField label="Name" /></GridColumn>
        <GridColumn label="Plan" sortable={false}><PlanTierField label="Plan" /></GridColumn>
        <BonusChipsField label="Active Bonuses" />
        <DateField source="updatedAt" label="Last Updated" showTime />
      </AdminDataGrid>
    </AdminList>
  );
}
